import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/config/api';
import {
  User,
  Activity,
  MessageSquare,
  Mail,
  Phone,
  X,
  Calendar,
  FileText,
  Pencil,
  Eye,
  Inbox,
  AlertTriangle,
  Loader2,
  Send,
  CheckCircle2,
  CalendarCheck,    // "Test scheduled" chip — pending
  ClipboardCheck,   // "Test completed" chip — done
} from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import { Textarea } from '@/Components/ui/textarea';
import { Label } from '@/Components/ui/label';
import { ScrollArea } from '@/Components/ui/scroll-area';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/Components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/Components/ui/dialog';
import { cn } from '@/lib/utils';
import { ResumeViewerDialog } from '../Applications/ResumePreview';

/**
 * CandidateCardTabbed
 *
 * The card you click on inside Job Details. It opens a Dialog with
 * tabs (Profile / Timeline / Feedback / Email / Contact). The compact
 * card itself shows just enough to identify the candidate; the heavy
 * lifting happens in the dialog.
 *
 * Timeline events are normalized client-side to a simpler taxonomy
 * because the raw API response includes scheduling-time entries we
 * deliberately hide here.
 */
const CandidateCardTabbed = ({
  candidate,
  jobId,
  job,
  recruiterEmail,
  onEditProfile,
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewingResume, setViewingResume] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  // ----- timeline fetch (lazy on tab open) ---------------------------------
  useEffect(() => {
    if (activeTab !== 'timeline' || !candidate?.id) return;

    let cancelled = false;
    const fetchTimeline = async () => {
      setTimelineLoading(true);
      setTimelineError(null);
      try {
        const params = new URLSearchParams({
          candidateId: candidate.id,
          jobId,
          recruiterEmail,
        });
        const res = await fetch(
          `${API_BASE_URL}/api/recruiter/candidate-timeline?${params}`,
          { credentials: 'include' },
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to load timeline');
        }
        const data = await res.json();
        if (!cancelled) setTimeline(normalizeTimeline(data.timeline));
      } catch (e) {
        console.error('Timeline fetch failed:', e);
        if (!cancelled) setTimelineError(e.message);
      } finally {
        if (!cancelled) setTimelineLoading(false);
      }
    };
    fetchTimeline();
    return () => {
      cancelled = true;
    };
  }, [activeTab, candidate?.id, jobId, recruiterEmail]);

  // The backend returns several event types; this card only shows the
  // ones useful for screening progress. Anything else is dropped.
  const normalizeTimeline = (events = []) =>
    events
      .map((event) => {
        const title = event.title?.toLowerCase() || '';
        const meta = event.metadata || {};

        if (title.includes('profile submitted')) {
          return {
            title: 'Resume Screening Done',
            description: event.description,
            timestamp: event.timestamp,
            color: '#2563eb',
          };
        }
        if (title.includes('candidate shortlisted')) {
          return {
            title: 'Shortlisted for Screening Round',
            description: event.description,
            timestamp: event.timestamp,
            color: '#f59e0b',
          };
        }
        if (title.includes('test created') || title.includes('test scheduled')) {
          const round = meta?.test_id === 'c' ? 'Coding' : 'Screening';
          return {
            title: `Shortlisted for ${round} Round`,
            description: `${round} test scheduled`,
            timestamp: event.timestamp,
            color: '#0891b2',
          };
        }
        if (title.includes('completed')) {
          const round = meta?.test_id === 'c' ? 'Coding' : 'Screening';
          return {
            title: `${round} Round Completed`,
            description: event.description,
            timestamp: event.timestamp,
            color: '#059669',
          };
        }
        // Drop "scheduled start time" entries — too noisy
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  // ----- helpers -----------------------------------------------------------
  const initials = (candidate.name || '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const status = candidate.status || 'Submitted';
  const statusTone =
    {
      hired:
        'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
      selected:
        'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
      shortlisted:
        'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400',
      submitted:
        'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      rejected:
        'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
    }[status.toLowerCase()] ||
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

  // ----- handlers ----------------------------------------------------------
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setIsRejecting(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/job_applications/${jobId}/reject`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            candidateId: candidate.id,
            reason: rejectReason,
            recruiterEmail,
          }),
        },
      );
      if (res.ok) {
        alert('Candidate rejected successfully');
        setShowRejectModal(false);
        setShowDetailModal(false);
        window.location.reload();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Failed to reject candidate');
      }
    } catch (err) {
      console.error('Error rejecting candidate:', err);
      alert('Failed to reject candidate. Please try again.');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleEmail = () => {
    const subject = `Regarding your application for ${job?.title || 'the position'}`;
    window.location.href = `mailto:${candidate.email}?subject=${encodeURIComponent(
      subject,
    )}`;
  };

  const handleCall = () => {
    if (candidate.phone) {
      window.location.href = `tel:${candidate.phone}`;
    } else {
      alert('No phone number available for this candidate');
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      {/* Compact card */}
      <Card
        className="cursor-pointer border border-border/40 transition-all hover:border-border/80 hover:shadow-sm"
        onClick={() => setShowDetailModal(true)}
      >
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {candidate.name || 'Unknown Candidate'}
              </p>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                Submitted{' '}
                {formatDate(candidate.created_at || candidate.submitted_on)}
              </p>
            </div>
          </div>

          {/* Test status chip — three states based on the backend's
              aggregated counts (across coding / screening / personality /
              behavioral / communication tests):

                tests_scheduled=0                  → no chip (nothing to say)
                tests_scheduled>0, completed=0     → "Test scheduled" (blue)
                completed>0, completed<scheduled   → "X/Y done" (amber)
                completed>0, completed==scheduled  → "Test completed" (green)
          */}
          <TestStatusChip
            scheduled={candidate.tests_scheduled || 0}
            completed={candidate.tests_completed || 0}
          />

          <Badge
            variant="outline"
            className={cn(
              'border-0 px-2 py-1 text-xs font-medium uppercase',
              statusTone,
            )}
          >
            {status}
          </Badge>
        </CardContent>
      </Card>

      {/* Detail modal */}
      <Dialog
        open={showDetailModal}
        onOpenChange={(o) => !o && setShowDetailModal(false)}
      >
        <DialogContent className="max-w-3xl p-0">
          <DialogHeader className="border-b border-border/40 px-6 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-base font-semibold text-primary">
                  {initials}
                </div>
                <div>
                  <DialogTitle className="text-base">
                    {candidate.name || 'Unknown Candidate'}
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    {job?.title || 'N/A'}
                  </DialogDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn(
                    'border-0 text-[10px] uppercase',
                    statusTone,
                  )}
                >
                  {status}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setShowRejectModal(true)}
                >
                  <X className="mr-1 h-3.5 w-3.5" />
                  Reject
                </Button>
              </div>
            </div>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="px-6">
            <TabsList className="my-3 grid w-full grid-cols-5">
              <TabsTrigger value="profile" className="gap-1.5">
                <User className="h-3.5 w-3.5" />
                <span>Profile</span>
              </TabsTrigger>
              <TabsTrigger value="timeline" className="gap-1.5">
                <Activity className="h-3.5 w-3.5" />
                <span>Timeline</span>
              </TabsTrigger>
              <TabsTrigger value="feedback" className="gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                <span>Feedback</span>
              </TabsTrigger>
              <TabsTrigger value="email" className="gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                <span>Email</span>
              </TabsTrigger>
              <TabsTrigger value="contact" className="gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                <span>Contact</span>
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="max-h-[55vh] pr-3">
              {/* PROFILE */}
              <TabsContent value="profile" className="mt-0 space-y-4 pb-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <ProfileItem
                    label="Email"
                    value={candidate.email || 'N/A'}
                  />
                  <ProfileItem
                    label="Phone"
                    value={candidate.phone || 'N/A'}
                  />
                  <ProfileItem
                    label="Experience"
                    value={
                      candidate.exp_yrs ? `${candidate.exp_yrs} years` : 'N/A'
                    }
                  />
                  <ProfileItem
                    label="Location"
                    value={candidate.cur_loc || 'N/A'}
                  />
                  <ProfileItem
                    label="Current CTC"
                    value={
                      candidate.cur_ctc ? `₹${candidate.cur_ctc} LPA` : 'N/A'
                    }
                  />
                  <ProfileItem
                    label="Expected CTC"
                    value={
                      candidate.exp_ctc ? `₹${candidate.exp_ctc} LPA` : 'N/A'
                    }
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onEditProfile(candidate);
                      setShowDetailModal(false);
                    }}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    Edit Profile
                  </Button>
                  {candidate.resume_path && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setViewingResume(true)}
                    >
                      <FileText className="mr-1.5 h-3.5 w-3.5" />
                      View Resume
                    </Button>
                  )}
                  {/* In-app viewer, nested in this dialog; the raw S3 link is never opened. */}
                  <ResumeViewerDialog
                    url={viewingResume ? candidate.resume_path : null}
                    candidateName={candidate.name || ''}
                    onClose={() => setViewingResume(false)}
                  />
                </div>
              </TabsContent>

              {/* TIMELINE */}
              <TabsContent value="timeline" className="mt-0 pb-4">
                {timelineLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full rounded-lg" />
                    ))}
                  </div>
                ) : timelineError ? (
                  <ErrorBlock message={timelineError} />
                ) : timeline.length === 0 ? (
                  <EmptyBlock
                    icon={Inbox}
                    text="No timeline events yet"
                  />
                ) : (
                  <div className="space-y-0">
                    {timeline.map((item, index) => {
                      const isLast = index === timeline.length - 1;
                      return (
                        <div key={index} className="relative flex gap-3 pb-4">
                          {!isLast && (
                            <div className="absolute left-[15px] top-8 h-full w-px bg-border" />
                          )}
                          <div
                            className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white shadow-sm"
                            style={{ backgroundColor: item.color }}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">
                              {item.title}
                            </div>
                            {item.description && (
                              <div className="text-xs text-muted-foreground">
                                {item.description}
                              </div>
                            )}
                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                              {formatDateTime(item.timestamp)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* FEEDBACK */}
              <TabsContent value="feedback" className="mt-0 space-y-3 pb-4">
                <h4 className="text-sm font-semibold">Internal Comments</h4>
                {candidate.comment ? (
                  <p className="rounded-md border border-border/40 bg-muted/30 p-3 text-sm">
                    {candidate.comment}
                  </p>
                ) : (
                  <p className="rounded-md border border-dashed border-border/40 p-3 text-sm text-muted-foreground">
                    No comments added yet
                  </p>
                )}
                <Button
                  size="sm"
                  onClick={() => {
                    onEditProfile(candidate);
                    setShowDetailModal(false);
                  }}
                >
                  Add / Edit Comment
                </Button>
              </TabsContent>

              {/* EMAIL */}
              <TabsContent value="email" className="mt-0 space-y-3 pb-4">
                <div className="flex items-center gap-3 rounded-md border border-border/40 bg-card p-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">
                    {candidate.email || 'No email available'}
                  </span>
                </div>
                <Button onClick={handleEmail} disabled={!candidate.email}>
                  <Send className="mr-2 h-4 w-4" />
                  Send Email
                </Button>
              </TabsContent>

              {/* CONTACT */}
              <TabsContent value="contact" className="mt-0 space-y-3 pb-4">
                <div className="flex items-center gap-3 rounded-md border border-border/40 bg-card p-3">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">
                    {candidate.phone || 'No phone number available'}
                  </span>
                </div>
                {candidate.phone && (
                  <Button onClick={handleCall}>
                    <Phone className="mr-2 h-4 w-4" />
                    Call Now
                  </Button>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Reject confirm modal */}
      <Dialog
        open={showRejectModal}
        onOpenChange={(o) => !o && !isRejecting && setShowRejectModal(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Candidate</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject{' '}
              <strong className="text-foreground">{candidate.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason for rejection</Label>
            <Textarea
              rows={4}
              placeholder="Please provide a reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              disabled={isRejecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isRejecting}
            >
              {isRejecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rejecting...
                </>
              ) : (
                'Reject Candidate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------

function ProfileItem({ label, value }) {
  return (
    <div className="rounded-md border border-border/40 bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium">{value}</div>
    </div>
  );
}

function EmptyBlock({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Icon className="h-10 w-10 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function ErrorBlock({ message }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <p className="text-sm text-destructive">{message}</p>
    </div>
  );
}

/**
 * Test status chip — tells the recruiter at a glance whether the
 * candidate has any tests on this job and whether they've been completed.
 *
 * The backend aggregates across all 5 test types (coding / screening /
 * personality / behavioral / communication) and returns two numbers:
 *
 *   scheduled — how many tests have this candidate in selected_users
 *   completed — of those, how many have a result row in test_results_base
 *
 * Display rules (kept simple — the goal is fast scanning, not detail):
 *   scheduled = 0                             → render nothing
 *   completed = 0,        scheduled > 0       → "Test scheduled" (blue)
 *   completed > 0, completed < scheduled      → "X/Y done"       (amber)
 *   completed = scheduled                     → "Test completed" (green)
 */
function TestStatusChip({ scheduled = 0, completed = 0 }) {
  if (!scheduled) return null;

  const allDone = completed >= scheduled;
  const partial = completed > 0 && completed < scheduled;

  let cls, Icon, label, tooltip;
  if (allDone) {
    cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    Icon = ClipboardCheck;
    label = scheduled === 1 ? 'Test completed' : `${completed} tests completed`;
    tooltip = `${completed} of ${scheduled} test${scheduled === 1 ? '' : 's'} completed.`;
  } else if (partial) {
    cls = 'bg-amber-50 text-amber-700 border-amber-200';
    Icon = CalendarCheck;
    label = `${completed}/${scheduled} done`;
    tooltip = `${completed} of ${scheduled} tests completed — ${scheduled - completed} pending.`;
  } else {
    cls = 'bg-sky-50 text-sky-700 border-sky-200';
    Icon = Calendar;
    label = scheduled === 1 ? 'Test scheduled' : `${scheduled} tests scheduled`;
    tooltip = `${scheduled} test${scheduled === 1 ? '' : 's'} assigned to candidate but not completed yet.`;
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
        cls,
      )}
      title={tooltip}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

export default CandidateCardTabbed;
